import React from 'react';
import type { TextRoleStyle } from './semanticTextRoles';

/**
 * WEB-3252 — рисунок вида «Столбцы» блока «График» (`chartKind: 'bars'`):
 * от двух до пяти столбцов слева направо на общей базовой линии.
 *
 *  · высота столбца пропорциональна `value` (проценты 0..100, вне диапазона
 *    зажимается — столбец не уезжает за рисунок);
 *  · выделенный (`highlight`) столбец закрашен акцентом блока, остальные — тем
 *    же акцентом с прозрачностью 0.25 и скруглением 6, как столбец «было» у
 *    вида `comparison` (`ComparisonSvg` в `ChartBlockSvg.tsx`);
 *  · `label` — под столбцом, `valueText` — над ним, только если задан;
 *  · меньше двух столбцов — рисунка нет (выдуманных столбцов тоже).
 *
 * Компонент получает уже готовые значения (подстановка переменных и чтение
 * обоих имён поля живут в ветке `chart` фасада отрисовки — там их видит
 * сторож контракта имён полей, см. докблок `ChartBlockSvg`).
 */

// Те же переменные темы, что у остальных видов графика (`ChartBlockSvg.tsx`).
const LABEL_COLOR = 'var(--color-text-secondary, #8c8c8c)';
const GRID_COLOR = 'var(--color-border-primary, #e8e8e8)';

const VIEW_W = 320;
const VIEW_H = 170;
const BASELINE = 136;
const MAX_BAR = 96;
const LEFT = 24;
const RIGHT = 296;
const MAX_BAR_WIDTH = 64;

export interface ChartBarView {
  id: string;
  label: string;
  value: number;
  valueText?: string;
  highlight?: boolean;
}

export interface ChartBarsSvgProps {
  accent: string;
  bars: readonly ChartBarView[];
  /** WEB-1692 юнит 8: роль `Secondary` — подписи под столбцами. */
  secondaryRole: TextRoleStyle;
}

function barHeight(value: number): number {
  const percent = Number.isFinite(value)
    ? Math.min(100, Math.max(0, value))
    : 0;
  return (percent / 100) * MAX_BAR;
}

export function ChartBarsSvg({
  accent,
  bars,
  secondaryRole,
}: ChartBarsSvgProps): React.ReactElement | null {
  if (bars.length < 2) return null;
  const slot = (RIGHT - LEFT) / bars.length;
  const width = Math.min(MAX_BAR_WIDTH, slot * 0.6);
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      style={{ display: 'block' }}
    >
      <line
        x1={LEFT}
        y1={BASELINE}
        x2={RIGHT}
        y2={BASELINE}
        style={{ stroke: GRID_COLOR }}
        strokeWidth={1.5}
      />
      {bars.map((bar, i) => {
        const height = barHeight(bar.value);
        const center = LEFT + slot * i + slot / 2;
        return (
          <g key={`${bar.id}-${i}`}>
            <rect
              x={center - width / 2}
              y={BASELINE - height}
              width={width}
              height={height}
              rx={6}
              style={
                bar.highlight
                  ? { fill: accent }
                  : { fill: accent, opacity: 0.25 }
              }
            />
            {bar.valueText && (
              <text
                x={center}
                y={BASELINE - height - 10}
                textAnchor="middle"
                fontSize={bar.highlight ? 14 : (secondaryRole.fontSize ?? 13)}
                fontWeight={bar.highlight ? 700 : 600}
                style={{
                  fill: bar.highlight
                    ? accent
                    : (secondaryRole.color ?? LABEL_COLOR),
                }}
              >
                {bar.valueText}
              </text>
            )}
            <text
              x={center}
              y={BASELINE + 22}
              textAnchor="middle"
              fontSize={secondaryRole.fontSize ?? 13}
              style={{ fill: secondaryRole.color ?? LABEL_COLOR }}
            >
              {bar.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
